// src/components/publishing/publication-history-page.tsx
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// The filter row is `Segmented` (item 09), the empty state is `EmptyState`
// (item 76), the rows are `ListRow`/`ListRows` (items 114, 115), the loading
// state is `Skeleton`, and the outcome chip is item 96's shape with the
// publication state set. `data-kit` is on the root.
//
// What did NOT move: `publishingApi.listHistory`, the filter, the title
// derivation, the relative date formatter and every string.
//
// What the swap removed: eleven hardcoded hex colours (`var(--a-brand-t)`, `var(--st-ok-t)`,
// `var(--a-gap-t)`, `var(--a-info-t)`, `var(--a-brand-t)`, `var(--a-reason-t)`) and four `rgba()` tints, none of
// which had a dark form. The destination marks keep a brand colour because a
// destination's colour IS its identity, and those three are now the only
// literals on the page and are declared once, in one map, with a comment
// saying why.
import { useEffect, useMemo, useState } from "react";
import {
  History, FileCode2, Globe, Linkedin, ExternalLink, Filter,
} from "lucide-react";
import { ModuleHeader } from "../shell/module-header";
import { toast } from "sonner";
import {
  publishingApi, Destination, destinationLabel,
  HistoryItem,
} from "@/app/services/publishing/publishing-api";
import {
  DeepLink, EmptyState, Icon, ListRow, ListRows, Segmented, SkeletonCard, cx,
} from "../kit";

interface PublicationHistoryPageProps {
  /** Currently active brand. History is filtered per-brand. */
  activeBrandName?: string;
}

export function PublicationHistoryPage({ activeBrandName }: PublicationHistoryPageProps) {
  // Alias so the existing `brandName` references below keep working.
  const brandName = activeBrandName;
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Destination | "all">("all");

  const load = async () => {
    setLoading(true);
    try {
      const list = await publishingApi.listHistory({
        brand_name: brandName,
        destination: filter === "all" ? undefined : filter,
        limit: 100,
      });
      setItems(list);
    } catch (e: any) {
      toast.error(e.message || "Could not load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandName, filter]);

  return (
    <div className="upm-page" data-kit>
      {/* Header (shared band) + destination filter */}
      <ModuleHeader
        page="Publication history"
        icon={History}
        compact
        collapsible
        title="Publication history"
        description="Every post published from Upmode, by destination."
      />

      <div className="shell-page upm-page__body">
        <div className="upm-page__filter">
          <Icon icon={Filter} size="sm" />
          <Segmented
            label="Destination"
            value={filter}
            onChange={(v) => setFilter(v as Destination | "all")}
            options={[
              { value: "all", label: "All" },
              { value: "hashnode", label: "Hashnode" },
              { value: "wordpress", label: "WordPress" },
              { value: "linkedin", label: "LinkedIn" },
            ]}
          />
        </div>

        {loading && items.length === 0 && <SkeletonCard />}

        {!loading && items.length === 0 && (
          <EmptyState
            icon={History}
            title="No posts published yet"
            description="Publish from Brand Publisher to see history here."
          />
        )}

        {items.length > 0 && (
          <ListRows>
            {items.map((item) => (
              <HistoryRow key={item.id} item={item} />
            ))}
          </ListRows>
        )}
      </div>
    </div>
  );
}

/* ── the outcome chip ──────────────────────────────────────────────────────
   Item 96's discipline with the publication state set: the LABEL and the TONE
   both come from the status, so a chip saying "Published" cannot be red. The
   kit's own `Badge` is not reused directly because its four states are run
   states — a publication that succeeded is not "Measured". Recorded in
   design/MISSING.md. */

function OutcomeChip({ ok }: { ok: boolean }) {
  return (
    <span className={cx("k-chip", "k-chip--status")} data-tone={ok ? "ok" : "error"}>
      {ok ? "Published" : "Failed"}
    </span>
  );
}

/* Destination marks. The glyph identifies the destination; the name is on the
   row two lines below it, so the mark does not also have to carry a brand
   colour. See publishing-page.css for why the three hexes went. */
const DESTINATION: Record<Destination, { icon: typeof FileCode2 }> = {
  hashnode:  { icon: FileCode2 },
  wordpress: { icon: Globe },
  linkedin:  { icon: Linkedin },
};

function HistoryRow({ item }: { item: HistoryItem }) {
  const meta = DESTINATION[item.destination];

  const snapshot = item.content_snapshot || {};
  const title = useMemo(() => {
    if (typeof snapshot.title === "string" && snapshot.title.trim()) {
      return snapshot.title.trim();
    }
    if (typeof snapshot.text === "string" && snapshot.text.trim()) {
      const firstLine = snapshot.text.trim().split("\n")[0];
      return firstLine.slice(0, 120) + (firstLine.length > 120 ? "…" : "");
    }
    return "(untitled)";
  }, [snapshot]);

  const succeeded = item.status === "published" || item.status === "sent";

  return (
    <ListRow
      className="upm-pub-row"
      title={
        <span className="upm-pub-row__head">
          <span className="upm-pub-row__mark">
            <Icon icon={meta.icon} size="sm" />
          </span>
          <span className="upm-pub-row__title">{title}</span>
        </span>
      }
      meta={
        <span className="upm-pub-row__meta">
          {destinationLabel(item.destination)}
          <OutcomeChip ok={succeeded} />
          <span>{formatDate(item.published_at)}</span>
          {!succeeded && item.error_message ? (
            <span className="upm-pub-row__error">{item.error_message}</span>
          ) : null}
          {item.external_id ? (
            <span className="upm-pub-row__id">ID {item.external_id}</span>
          ) : null}
        </span>
      }
      trailing={
        item.external_url ? (
          <DeepLink href={item.external_url} target="_blank" rel="noreferrer" icon={ExternalLink}>
            View
          </DeepLink>
        ) : undefined
      }
    />
  );
}

function formatDate(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 1) return "just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}
